import { DataItem } from '../components/CollectionAndSequencing/Demographics/Demographics.types';

import {
  LegendStackedBar,
  LEGEND_COLOR,
} from '../components/CollectionAndSequencing/Overview/ConsentTrend/SamplesSequencedInfo/SamplesSequenced.types';
import { SequencingSamplesSerie } from '../types';
import { Composition, SamplesSequenced } from '../types/overview.types';
import { DateRangeOptionId, DateRangeSelection } from '@builder';
import {
  buildDynamicDateRange,
  buildDailyData,
  buildMonthlyData,
  DATE_FORMAT_DAILY,
  DATE_FORMAT_MONTHLY,
} from './sampleViewGraph';
import { getDateWithoutTZ } from '@common/utils';
import { ChartGroupTotals } from '@common/types';
import { SingleValueBarFormat } from '@charts';

export const formatStackedBarData = (data: Composition): SingleValueBarFormat[] => {
  // eslint-disable-next-line no-use-before-define
  const newData: any = {};

  data.series.forEach(({ groupValue, value }) => {
    groupValue = getDateWithoutTZ(groupValue, DATE_FORMAT_MONTHLY);
    if (newData[groupValue] !== undefined && newData[groupValue]) {
      newData[groupValue].value = value;
    } else {
      newData[groupValue] = {
        groupValue,
        value,
      };
    }
  });

  const formattedData: SingleValueBarFormat[] = Object.values(newData);

  return formattedData;
};

export const formatStackedBarInfoLegend = (data: SamplesSequenced): LegendStackedBar[] => {
  const legend = data.metric.series
    .filter(({ status }, index, self) => index === self.findIndex((selfStatus) => selfStatus.status === status))
    .map(({ status }) => {
      return {
        color: LEGEND_COLOR[status],
        label: status,
      };
    });

  return legend;
};

export const groupDemographicsData = (data: DataItem[]): ChartGroupTotals[] => {
  const totalValue = data.reduce((acc, curr) => acc + curr.value, 0);

  const groupTotalsMap = data.reduce((acc, curr) => {
    const { groupValue, value } = curr;

    // eslint-disable-next-line
    const existingTotal = acc.get(groupValue)?.total || 0;

    return acc.set(groupValue, {
      name: groupValue,
      total: existingTotal + value,
      percentage: 0,
    });
  }, new Map<string, ChartGroupTotals>());

  const groupTotals = Array.from(groupTotalsMap.values());
  const totalPercentage = groupTotals.reduce((acc, curr) => acc + curr.total / totalValue, 0);

  groupTotals.forEach((groupTotal) => {
    groupTotal.percentage = ((groupTotal.total / totalValue) * 100) / totalPercentage;
  });

  return groupTotals;
};

export const formatResultsDeliveredBarData = (data: SequencingSamplesSerie[]): SingleValueBarFormat[] => {
  // eslint-disable-next-line no-use-before-define
  const newData: any = {};
  data.sort((a, b) => new Date(a.groupValue).getTime() - new Date(b.groupValue).getTime());
  data.forEach(({ groupValue, value }) => {
    groupValue = getDateWithoutTZ(groupValue, DATE_FORMAT_MONTHLY);
    if (newData[groupValue]) {
      newData[groupValue].value += value;
    } else {
      newData[groupValue] = {
        groupValue,
        value,
      };
    }
  });

  const formattedData: SingleValueBarFormat[] = Object.values(newData);
  return formattedData;
};

export const formatSamplesViewGraph = (
  data: SequencingSamplesSerie[],
  { endDate, selectedDateRange, startDate }: DateRangeSelection,
): SingleValueBarFormat[] => {
  let dateFormat: string;
  // eslint-disable-next-line no-use-before-define
  let newData: Record<string, SingleValueBarFormat> = {};

  const sortedData: SequencingSamplesSerie[] = data.sort((a, b) =>
    new Date(a.groupValue) > new Date(b.groupValue) ? 1 : -1,
  );

  if (!startDate || !endDate) {
    throw new Error('startDate or endDate must not be empty.');
  }

  switch (selectedDateRange) {
    case DateRangeOptionId.THIS_WEEK:
    case DateRangeOptionId.THIS_MONTH:
    case DateRangeOptionId.LAST_MONTH:
      dateFormat = DATE_FORMAT_DAILY;
      newData = buildDailyData(sortedData, startDate, endDate, dateFormat);
      break;
    case DateRangeOptionId.THIS_YEAR:
      dateFormat = DATE_FORMAT_MONTHLY;
      newData = buildMonthlyData(sortedData, startDate, endDate, dateFormat);
      break;
    case DateRangeOptionId.ALL_TIME:
      // eslint-disable-next-line no-case-declarations
      const allTimeData = buildDynamicDateRange(sortedData, startDate, endDate, {
        dailyDateFormat: 'EEE MMM d yyyy',
      });

      dateFormat = allTimeData.dateFormat;
      newData = allTimeData.data;
      break;
    case DateRangeOptionId.CUSTOM:
      // eslint-disable-next-line no-case-declarations
      const customData = buildDynamicDateRange(sortedData, startDate, endDate);

      dateFormat = customData.dateFormat;
      newData = customData.data;
      break;
  }

  const formattedData = Object.values(newData);
  return formattedData;
};
